import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import React, { useEffect, useMemo, useState } from "react";
import { fetchApplicationById } from "../../store/slices/applicationDetailsSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomBreadcrumb from "../CustomBreadcrumb/CustomBreadcrumb";
import { Box } from "@mui/material";
import {
  Checkbox,
  LoadingSpinner,
  TextArea,
} from "cprime-foundational-components";
import Button from "../Common/CprimeButton";
import { triggerToast } from "../../utils/toast";
import {
  uploadPolicyJSON,
  restoreVersion,
  promotePolicyJSON,
} from "../../services/RequestAccess/RequestAccess";
import * as style from "./Publish.module.scss";
import RoleComparison from "./RoleComparison";
import ViewPublishApp from "./ViewPublishApp";

const PublishAction = ({
  breadCrumbLabel = "",
  header = "",
  headerText = "",
  selectedTab = 9,
  isPublish = true,
  isView = false,
}) => {
  const scssClass = style as any;

  const { id, action } = useParams();
  const [contextForm, setContextForm] = useState<any>({
    comment: "",
    is_encrypted: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [newCode, setNewCode] = useState<any>(JSON.stringify({}));

  const { data } = useSelector(
    (state: RootState) => state?.applicationDetails ?? { data: {} },
  );
  const { env } = useSelector(
    (state: any) => state?.createApplication ?? { env: "" },
  );

  const appId = useMemo(() => {
    return id?.split("-")?.[1];
  }, [id]);

  const location = useLocation();

  // Use URLSearchParams to parse the query string
  const queryParams = new URLSearchParams(location.search);
  const versionId = queryParams.get("version");
  const uploadId = queryParams.get("uploadId");

  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const handleChange = (field: string, value: string) => {
    setContextForm({ ...contextForm, [field]: value });
  };

  const isFormValid = () => {
    if (breadCrumbLabel === "Review Request") {
      return !isChecked || !contextForm?.comment;
    }
    return !contextForm?.comment;
  };

  const publishPolicyJson = async () => {
    try {
      if (!newCode || !contextForm?.comment) return;
      const res = await uploadPolicyJSON(
        {
          filename: "policy_json",
          data: newCode,
          comment: contextForm?.comment,
          requiresApproval: true,
          is_encrypted: Boolean(contextForm?.is_encrypted),
        },
        appId,
      );
      if (res?.status === "SUCCESS") {
        triggerToast(res?.message, "success");
        navigate(`/application-list/app-${appId}`, {
          state: { selectedTab },
        });
      } else {
        triggerToast(res?.message, "error");
      }
    } catch (err: any) {
      triggerToast(err?.message || "Something went wrong", "error");
    }
  };

  const importPolicy = async () => {
    try {
      if (!uploadId || !contextForm?.comment) return;
      const res = await promotePolicyJSON(
        {
          uploadId,
          comment: contextForm?.comment,
        },
        action === "import" ? `/promote/import/${appId}` : "/promote/restore",
      );
      if (res?.status === "SUCCESS") {
        triggerToast(res?.message, "success");
        navigate(`/application-list/app-${appId}`, {
          state: { selectedTab },
        });
      } else {
        triggerToast(res?.message, "error");
      }
    } catch (err: any) {
      triggerToast(err?.message || "Something went wrong", "error");
    }
  };

  const restorePublishVersion = async (isRequest: string | boolean = false) => {
    try {
      if (!contextForm?.comment) return;
      const res = await restoreVersion(
        {
          ...(uploadId && { uploadId }),
          comment: contextForm?.comment,
          ...(isRequest && { action: isRequest }),
          ...(action !== "withdraw" && { requiresApproval: true }),
          is_encrypted:
            Boolean(data?.is_encrypted) || Boolean(contextForm?.is_encrypted),
        },
        versionId,
      );
      if (res?.status === "SUCCESS") {
        triggerToast(res?.message, "success");
        navigate(`/application-list/app-${appId}`, {
          state: { selectedTab },
        });
      } else {
        triggerToast(res?.message, "error");
      }
    } catch (err: any) {
      triggerToast(err?.message || "Something went wrong", "error");
    }
  };

  const buildReviewUrl = () => {
    if (isPublish && versionId) return `?versionId=${versionId}`;
    if (!isPublish && uploadId) return `?uploadId=${uploadId}`;
    return "";
  };

  const handleSubmit = async (action?: string) => {
    try {
      setIsLoading(true);
      if (breadCrumbLabel === "Approve" || breadCrumbLabel === "Withdraw")
        await restorePublishVersion(action);
      else if (uploadId) await importPolicy();
      else if (versionId) await restorePublishVersion(false);
      else await publishPolicyJson();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (appId) {
      dispatch(fetchApplicationById(Number(appId)) as any);
    }
  }, [appId, dispatch, versionId, isPublish]);

  return (
    <>
      <div className={scssClass?.publishActionContainer}>
        <CustomBreadcrumb
          items={[
            {
              label: "Application List",
              href: "/application-list",
              icon: <HomeOutlinedIcon sx={{ color: "#2469FF" }} />,
            },
            {
              label: `${data?.app_cmdb_name}`,
              href: `/application-list/app-${data?.id}`,
            },
            {
              label: breadCrumbLabel,
              href: undefined,
            },
          ]}
        />

        <Box>
          <Box className={scssClass.headerText}>{header}</Box>
          <Box className={scssClass.headerText} sx={{ marginBottom: "5rem" }}>
            <Box
              sx={{
                maxHeight: `calc(100vh - ${isView ? 376 : breadCrumbLabel === "Review Request" ? 548 : 492}px)`,
                overflow: "auto",
                marginBottom: "1rem",
              }}
            >
              <div>{headerText}</div>
              <Box marginBottom={isView ? 1 : 2.5} marginTop={2}>
                {isView ? (
                  <ViewPublishApp
                    url={`/publish/version-detail/${appId}${buildReviewUrl()}`}
                  />
                ) : (
                  <RoleComparison
                    setNewCode={setNewCode}
                    url={`/${isPublish ? "publish" : "promote"}/review/${appId}${buildReviewUrl()}`}
                    payload={null}
                    title={"Role Comparison"}
                  />
                )}
              </Box>
            </Box>
            {isView ? null : (
              <TextArea
                fullWidth
                placeholder="Input Text"
                label="Comment"
                value={contextForm?.comment}
                onChange={(e) => handleChange("comment", e?.target?.value)}
                maxLength={100}
              />
            )}
            {!isView && breadCrumbLabel === "Approve" && (
              <Box marginTop={0.5}>
                <Checkbox
                  label="Encrypt policy JSON"
                  checked={
                    Boolean(data?.is_encrypted) ||
                    Boolean(contextForm?.is_encrypted)
                  }
                  onChange={() =>
                    setContextForm((prev: any) => ({
                      ...prev,
                      is_encrypted: !prev?.is_encrypted,
                    }))
                  }
                  disabled={Boolean(data?.is_encrypted)}
                />
              </Box>
            )}
            {breadCrumbLabel === "Review Request" && (
              <Box marginTop={0.5}>
                <Checkbox
                  label={`I understand publishing the application will apply the changes to the ${env} environment`}
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                />
              </Box>
            )}
          </Box>
        </Box>
      </div>
      <div className={scssClass?.footerButtons}>
        <div>
          <Button
            variant="tertiary"
            sx={{ margin: "0 10px" }}
            size="medium"
            onClick={() =>
              navigate(`/application-list/app-${data?.id}`, {
                state: { selectedTab },
              })
            }
          >
            Cancel
          </Button>
          {isView ? null : (
            <Button
              variant="primary"
              sx={{ margin: "0 10px" }}
              size="medium"
              onClick={() =>
                handleSubmit(
                  breadCrumbLabel === "Approve" ||
                    breadCrumbLabel === "Withdraw"
                    ? breadCrumbLabel.toLowerCase()
                    : undefined,
                )
              }
              disabled={isFormValid()}
            >
              {breadCrumbLabel}
            </Button>
          )}
          {breadCrumbLabel === "Approve" ? (
            <Button
              variant="danger"
              sx={{ margin: "0 10px" }}
              size="medium"
              onClick={() => handleSubmit("deny")}
              disabled={isFormValid()}
            >
              Deny
            </Button>
          ) : null}
        </div>
      </div>
      {isLoading ? (
        <div className={scssClass?.loaderSpinner}>
          <LoadingSpinner />
        </div>
      ) : null}
    </>
  );
};

export default PublishAction;
